import { useState, type MouseEvent } from 'react';
import { Avatar, Box, ButtonBase, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '@/shared/auth/useAuth';
import { getInitials } from './getInitials';
import { getPersonaMenuOrigins } from './getPersonaMenuOrigins';

export interface SidebarPersonaCardProps {
  /** Drawer mini-variant — icon-only trigger, menu anchors from the rail center. */
  readonly collapsed?: boolean;
}

/**
 * Humanize a role slug (`sales_rep` → `Sales Rep`) for the card's subtitle.
 * Deliberately local/tiny rather than importing the admin module's
 * `ROLE_LABELS` map — that lives in `modules/admin` for manual-grant UI and
 * pulling a shared/layout dependency on it the wrong way across module
 * boundaries for one label isn't warranted.
 */
const roleLabel = (role: string): string =>
  role
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');

// A user can hold several roles at once (e.g. the baseline `basic_user` floor
// PLUS a granted `sales_rep`). Show the HIGHEST-privilege one — not `roles[0]`,
// whose order is not guaranteed and made a Sales Rep display as "Basic User".
// Highest → lowest; unknown/legacy values fall through to the first role.
const ROLE_PRIORITY: readonly string[] = [
  'admin',
  'sales_rep',
  'basic_user',
  // legacy tiers, ranked below the canonical SA-2 set
  'sales_leader',
  'account_manager',
  'finance',
  'consultant',
  'read_only',
];

const personaSubtitle = (roles: readonly string[]): string => {
  if (roles.length === 0) return '';
  const best = ROLE_PRIORITY.find((r) => roles.includes(r)) ?? roles[0];
  return roleLabel(best);
};

/**
 * Persona control pinned to the bottom of the sidebar Drawer — relocated
 * here from `TopBar`'s top-right avatar (2026-08 nav restructure). Clicking
 * it opens the exact same two-item menu the old TopBar menu had (a disabled
 * display-name row + "Sign out"); nothing else is added.
 *
 * Two behaviors here are load-bearing, not decorative:
 *
 *  - The root trigger element (`Tooltip` → `ButtonBase`) is the SAME JSX
 *    branch regardless of `collapsed` — only the inner name/subtitle text
 *    is conditionally omitted. If collapsed/expanded instead rendered two
 *    structurally different trees, toggling the sidebar while this menu is
 *    open would remount the trigger mid-open and silently drop the Menu's
 *    `anchorEl`, closing it out from under the user.
 *  - The Menu's anchor/transform origins are computed from `collapsed`,
 *    not copied from the old TopBar values — those were tuned for a
 *    top-right trigger with room below; this trigger sits at the bottom of
 *    the screen, so the menu must open upward, and its horizontal origin
 *    must re-center when the sidebar is the narrow icon rail.
 */
export const SidebarPersonaCard = ({
  collapsed = false,
}: SidebarPersonaCardProps): JSX.Element => {
  const { user, logout } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLElement>): void => setAnchor(event.currentTarget);
  const handleClose = (): void => setAnchor(null);
  const handleLogout = async (): Promise<void> => {
    setAnchor(null);
    await logout();
  };

  const displayName = user?.displayName ?? 'Unknown';
  const initials = getInitials(user?.displayName);
  const subtitle = personaSubtitle(user?.roles ?? []);
  const { anchorOrigin, transformOrigin } = getPersonaMenuOrigins(collapsed);

  return (
    <Box sx={{ px: collapsed ? 0.5 : 1, py: 1 }}>
      <Tooltip title={collapsed ? displayName : ''} placement="right" arrow>
        <ButtonBase
          onClick={handleOpen}
          aria-label="Open user menu"
          aria-haspopup="true"
          aria-expanded={Boolean(anchor)}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: collapsed ? 0 : 1.5,
            py: 1,
            borderRadius: 1,
            justifyContent: collapsed ? 'center' : 'flex-start',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
            {initials}
          </Avatar>
          {!collapsed ? (
            <>
              <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {displayName}
                </Typography>
                {subtitle ? (
                  <Typography
                    variant="caption"
                    noWrap
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.3 }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
              <ChevronRightIcon
                fontSize="small"
                sx={{ color: 'text.secondary', flexShrink: 0 }}
              />
            </>
          ) : null}
        </ButtonBase>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <MenuItem disabled>{displayName}</MenuItem>
        <MenuItem onClick={handleLogout}>Sign out</MenuItem>
      </Menu>
    </Box>
  );
};
